import React, { useState } from 'react';
import { Menu, Popover, Tab } from 'glints-aries/es/@next';
import { matchPath, useHistory, useLocation } from 'react-router-dom';

import useGetRefCodeParam from '../../common/hooks/useGetRefCodeParam';
import { MenuItem } from '../Platform/Constants';
import { HeaderNavigation } from './NavbarDesktop.sc';
import { disableLink, isAbsoluteURL, renderItemLabel } from './navigationUtils';

type Props = {
  company: unknown;
  intl: Parameters<typeof renderItemLabel>[0];
  items: MenuItem[];
  permissions: unknown;
  role: string;
};

export const isHeaderMenuItemActive = (pathname: string, item: MenuItem) => {
  const paths = item.children?.map(child => child.to) ?? [item.to];
  return paths.some(
    path =>
      path &&
      !isAbsoluteURL(path) &&
      Boolean(
        matchPath(pathname, {
          path,
          exact: path === '/' || path === '/dashboard',
        })
      )
  );
};

export default function DesktopHeaderNavigation({
  company,
  intl,
  items,
  permissions,
  role,
}: Props) {
  const history = useHistory();
  const { pathname } = useLocation();
  const referralCode = useGetRefCodeParam();
  const [openMenu, setOpenMenu] = useState<string>();

  const navigate = (item: MenuItem) => {
    if (
      item.disabled ||
      (item.canBeDisabled && disableLink(role, company, permissions))
    ) {
      return;
    }

    item.onClick?.();
    if (!item.to) return;

    if (item.isExternal || isAbsoluteURL(item.to)) {
      window.open(item.to, '_blank', 'noopener,noreferrer');
      return;
    }

    history.push({
      pathname: item.to,
      ...(referralCode && { search: `refCode=${referralCode}` }),
    });
  };

  return (
    <HeaderNavigation aria-label="Primary navigation">
      {items.map(item => {
        const disabled =
          item.disabled ||
          (item.canBeDisabled && disableLink(role, company, permissions));
        const tab = (
          <Tab
            id={`header-navigation-${item.value}`}
            content={renderItemLabel(intl, item.label)}
            selected={isHeaderMenuItemActive(pathname, item)}
            disabled={disabled}
            disabledScrollToTab={true}
            onSelect={() =>
              item.children
                ? setOpenMenu(openMenu === item.value ? undefined : item.value)
                : navigate(item)
            }
          />
        );

        if (!item.children)
          return <React.Fragment key={item.value}>{tab}</React.Fragment>;

        return (
          <Popover
            key={item.value}
            active={openMenu === item.value}
            activator={tab}
            onClose={() => setOpenMenu(undefined)}
            preferredPosition="below"
            preferredAlignment="left"
          >
            <Popover.Pane>
              <Menu
                options={item.children.map(child => ({
                  value: child.value,
                  label: renderItemLabel(intl, child.label),
                  disabled:
                    child.disabled ||
                    (child.canBeDisabled &&
                      disableLink(role, company, permissions)),
                }))}
                selectedValues={[]}
                onClick={({ value }) => {
                  const selectedItem = item.children?.find(
                    child => child.value === value
                  );
                  setOpenMenu(undefined);
                  if (selectedItem) navigate(selectedItem);
                }}
              />
            </Popover.Pane>
          </Popover>
        );
      })}
    </HeaderNavigation>
  );
}
